import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Trash2, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const [gallery, setGallery] = useState([]);
  const [file, setFile] = useState(null);
  const [style, setStyle] = useState('');
  const [uploading, setUploading] = useState(false);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/gallery`);
      setGallery(response.data);
    } catch (error) {
      toast.error('Failed to load gallery');
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please select an image');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('style', style || 'Custom Design');

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${BACKEND_URL}/api/admin/gallery`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Image uploaded successfully!');
      setFile(null);
      setStyle('');
      fetchGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/gallery/${itemId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Image deleted successfully');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  return (
    <div>
      <h2 className="text-3xl font-black text-white mb-8 uppercase">Gallery Management</h2>

      {/* Upload Form */}
      <Card className="bg-zinc-900 border-zinc-800 mb-8">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Upload className="w-5 h-5 text-red-600" />
            Upload New Tattoo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white font-bold uppercase text-sm">Image File *</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files[0])}
                className="bg-zinc-800 border-zinc-700 text-white"
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white font-bold uppercase text-sm">Style/Category</Label>
              <Input
                type="text"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="e.g., Tribal, Realism, Fine Line"
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-gray-500"
              />
            </div>

            <Button
              type="submit"
              disabled={uploading}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold uppercase"
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Gallery Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {gallery.map((item) => (
          <Card key={item.id} className="bg-zinc-900 border-zinc-800 group">
            <CardContent className="p-0">
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={`${BACKEND_URL}${item.image_url}`}
                  alt={item.style}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <Button
                    onClick={() => handleDelete(item.id)}
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
              <div className="p-4">
                <p className="text-red-500 font-bold uppercase text-sm">{item.style}</p>
                <p className="text-gray-500 text-xs mt-1">
                  {new Date(item.created_at).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {gallery.length === 0 && (
        <div className="text-center py-20">
          <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No images uploaded yet</p>
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
